/**
 * The seven modules, and what each one owns.
 *
 * This replaces the old flat Work / Delegation / Manage sections. The rail
 * is now the top-level map of the product: pick a module, get that module's
 * own navigation beside it. One list, read by the rail, the panel AND the
 * login picker, so the three can never disagree about what exists.
 *
 * GATING IS COURTESY, NOT CONTROL. `gate` hides a module somebody cannot
 * use - absent, not greyed, because a door you cannot open is noise on a
 * map. The API's 403 is the actual control and must stay that way.
 */
import { Permission } from '@todo/shared';

export type ModuleId = 'dashboard' | 'todo' | 'sign' | 'meetings' | 'vault' | 'creator' | 'audit';

export interface ModuleItem {
  label: string;
  to: string;
  icon: string;
  /** ADMIN and SUPER_ADMIN only. */
  adminOnly?: boolean;
  /**
   * SUPER_ADMIN only - stricter than `adminOnly`, and the narrower of the
   * two wins when both are set.
   *
   * For a page inside an admin module that an ordinary Admin must not
   * reach. Recipient Groups is one: a group silently decides who gets
   * invited to things, so editing one is a narrower right than the
   * Departments and Projects sitting beside it.
   */
  superAdminOnly?: boolean;
  /**
   * A per-user grant the person must hold (user_permissions - e.g. lottery.view). Lottery access is granted person by
   * person, not by role, so a role flag cannot express it. Courtesy only: every page and the API re-check.
   */
  permission?: string;
  /** A small heading the panel shows above the first item of a group (the Lottery pages inside To-Do). */
  group?: string;
}

export interface AppModule {
  id: ModuleId;
  label: string;
  /** One line on the login picker - what the module is for. */
  blurb: string;
  icon: string;
  /** Where clicking the module takes you. */
  home: string;
  /**
   * The module's own navigation. EMPTY means the module is a single screen
   * and the panel is hidden entirely - a rail plus a panel holding one link
   * is worse than no panel.
   */
  items: ModuleItem[];
  gate?: 'admin' | 'superAdmin';
}

export const MODULES: AppModule[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    blurb: 'What needs you today, across To-Do, Sign, Meetings and Vault',
    icon: 'dashboard',
    home: '/dashboard',
    items: [],
  },
  {
    id: 'todo',
    label: 'To-Do',
    blurb: 'Your day, your tasks and what you have delegated',
    icon: 'list',
    home: '/my-day',
    items: [
      { label: 'My Day', to: '/my-day', icon: 'sun' },
      { label: 'Tasks', to: '/tasks', icon: 'list' },
      { label: 'Important', to: '/important', icon: 'star' },
      { label: 'Planned', to: '/planned', icon: 'clock' },
      { label: 'Assigned by me', to: '/assigned-by-me', icon: 'send', adminOnly: true },
      { label: 'Assigned to me', to: '/assigned-to-me', icon: 'inbox' },
      { label: 'Lists', to: '/lists', icon: 'folder' },
      // Lottery lives inside To-Do: the page for lottery.view; Daily upload and Stockists & schemes for lottery.upload
      // (which always comes with view).
      { label: 'Lottery', to: '/lottery', icon: 'chart', permission: Permission.LOTTERY_VIEW, group: 'Lottery' },
      { label: 'Daily upload', to: '/lottery/upload', icon: 'upload', permission: Permission.LOTTERY_UPLOAD, group: 'Lottery' },
      { label: 'Stockists & schemes', to: '/lottery/stockists', icon: 'review', permission: Permission.LOTTERY_UPLOAD, group: 'Lottery' },
    ],
  },
  {
    id: 'sign',
    label: 'CG Sign',
    blurb: 'Send documents for signature and track who has signed',
    icon: 'send',
    home: '/cg-sign',
    items: [],
  },
  {
    id: 'meetings',
    label: 'Meetings',
    blurb: 'Your calendar, invitations and public engagements',
    icon: 'calendar',
    home: '/calendar',
    items: [],
  },
  {
    id: 'vault',
    label: 'CG Vault',
    blurb: 'Share files with a person, a department or everyone',
    icon: 'folder',
    home: '/vault',
    items: [],
  },
  {
    id: 'creator',
    label: 'Creator',
    blurb: 'Set up the people, departments and projects you work through',
    icon: 'tools',
    home: '/people',
    gate: 'admin',
    items: [
      { label: 'People', to: '/people', icon: 'people' },
      { label: 'Departments', to: '/departments', icon: 'folder' },
      { label: 'Recipient Groups', to: '/recipient-groups', icon: 'people', superAdminOnly: true },
      { label: 'Projects', to: '/projects', icon: 'folder' },
      { label: 'Guests', to: '/guests', icon: 'people' },
    ],
  },
  {
    id: 'audit',
    label: 'Audit Trail',
    blurb: 'Who changed what, who opened what, and when',
    icon: 'shield',
    home: '/audit',
    gate: 'superAdmin',
    items: [],
  },
];

/**
 * The items of one module this person may see.
 *
 * ONE implementation, because there were two: the Sidebar filtered the
 * list it renders and AppLayout filtered the same list again to decide
 * whether to show the panel at all. They agreed only by coincidence, and
 * the moment a second gate (`superAdminOnly`) arrived they would have
 * disagreed - an Admin getting an empty panel on a module whose only
 * remaining page is Super-Admin-only.
 *
 * Both flags are checked, so the NARROWER one wins when both are set.
 */
export function visibleModuleItems(
  items: ModuleItem[] | undefined,
  systemKey: string | null | undefined,
  /** The person's grants (the profile's `permissions`); an item with `permission` needs it. */
  permissions: readonly string[] = [],
): ModuleItem[] {
  const isAdmin = systemKey === 'ADMIN' || systemKey === 'SUPER_ADMIN';
  const isSuperAdmin = systemKey === 'SUPER_ADMIN';
  return (items ?? []).filter(
    (item) =>
      (!item.adminOnly || isAdmin) && (!item.superAdminOnly || isSuperAdmin)
      && (!item.permission || permissions.includes(item.permission)),
  );
}

/** The modules this person may see. */
export function visibleModules(systemKey: string | null | undefined): AppModule[] {
  const isAdmin = systemKey === 'ADMIN' || systemKey === 'SUPER_ADMIN';
  const isSuperAdmin = systemKey === 'SUPER_ADMIN';
  return MODULES.filter((m) =>
    m.gate === 'superAdmin' ? isSuperAdmin : m.gate === 'admin' ? isAdmin : true,
  );
}

/**
 * Which module a path belongs to, so the rail highlights correctly on a
 * deep link or a browser refresh - the rail cannot rely on what was last
 * clicked.
 *
 * Longest prefix wins: '/people' must not claim '/planned', so the match is
 * on a path SEGMENT boundary rather than a bare startsWith.
 */
export function moduleForPath(path: string): ModuleId {
  const onPath = (to: string) => path === to || path.startsWith(`${to}/`);
  for (const m of MODULES) {
    if (onPath(m.home) || m.items.some((i) => onPath(i.to))) return m.id;
  }
  return 'todo';
}
